import type { BlobArrayType } from "@pdfking/blob"
import { BlobArray, BlobToUint8 } from "@pdfking/blob"
import type { StreamInput } from "@pdfking/io"
import { StreamToIterable } from "@pdfking/io"
import type { Bytes } from "@pdfking/structs"
import { Join } from "@pdfking/tfunc"

export type StreamBufferType = ReturnType<typeof StreamBuffer>

// The input as it downloads, appended as it comes to the one `bytes` a reader
// holds on to — pdfium reads it synchronously, so a blob is read in as it is
// pulled, and bytes stay the views they came as — and pulled only as far as a
// reader asks: `pull` reads at least as many more bytes as it is told —
// `chunk_size` unless told otherwise, since parts arrive as small as a network
// read and looking again after every one costs more than the bytes it waits
// for — whatever size the parts arrive in, `take` reads the few bytes it hands
// over, `slice` hands them over as views, `skip` forgets them as they arrive,
// and `close` stops the download once the reader is done.
export const StreamBuffer = (stream: StreamInput, chunk_size: Bytes) => {
  const iter = Join(StreamToIterable(stream))
  const bytes = BlobArray()

  // one more part in, false once there are none
  const next = (): Promise<boolean> =>
    iter.next().then(({ done, value }) => {
      if (value) bytes.push(value)
      return !done
    })

  const fill = async (until: number): Promise<void> => {
    while (bytes.size < until && (await next())) {}
  }

  // everything from `start` on, in place: the same `bytes` for whoever holds it
  const drop = (start: number): void => {
    const rest = bytes.slice(start, bytes.size)
    bytes.clear()
    bytes.push(rest)
  }

  // the first `length` bytes as views, pulled if they are not in yet, then
  // forgotten — fewer, or none, once the stream has ended
  const cut = async (length: number): Promise<BlobArrayType> => {
    await fill(length)
    const head = bytes.slice(0, length)
    drop(length)
    return head
  }

  return {
    bytes,
    // at least `length` more bytes in, or what was left: false once nothing
    // more came, so a reader asks again about whatever did
    pull: async (length: number = chunk_size): Promise<boolean> => {
      const before = bytes.size
      await fill(before + length)
      return bytes.size > before
    },
    take: (length: number): Promise<Uint8Array> => cut(length).then(BlobToUint8),
    slice: cut,
    // dropped as they arrive: nothing skipped is held longer than its part,
    // and past the end there is nothing left to drop
    skip: async (length: number): Promise<void> => {
      while (bytes.size < length) {
        length -= bytes.size
        bytes.clear()
        if (!(await next())) return
      }
      drop(length)
    },
    close: async (): Promise<void> => {
      bytes.clear()
      await iter.return(undefined)
    },
  }
}
